import { Button, Link, TextField, Typography } from "@hellocarbo/lasagne";
import { ChangeEvent, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import background from "@/assets/fr/sign-up/background.png";
import logo from "@/assets/logo.svg";
import Carousel from "@/components/SignUp/Carousel";

import PasswordConstraints from "./PasswordConstraints";

const SignUp = () => {
  const { t } = useTranslation(["common", "signUp"]);

  const [searchParams, setSearchParams] = useSearchParams();
  const step = searchParams.get("step") ? Number(searchParams.get("step")) : 1;

  const [email, setEmail] = useState("");
  const [hasEmailError, setHasEmailError] = useState(false);
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const isNewSignup =
    // Can't rely on the data from the feature context since it's not reliable for AB tests
    document.getElementById("new_signup_page")?.dataset.enabled === "true";

  useEffect(() => {
    if (step > 1 && !email) {
      setSearchParams({ step: "1" });
    }
  }, [step, email, setSearchParams]);

  if (!isNewSignup) {
    return <PasswordConstraints />;
  }

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleContinue = () => {
    // naive client side email format validation
    const regex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (regex.test(email)) {
      setSearchParams({ step: "2" });
    } else {
      setHasEmailError(true);
    }
  };

  if (step === 1) {
    return (
      <div className="gap-lg p-xl relative grid h-screen w-screen grid-cols-2 items-center justify-center overflow-hidden">
        <div className="mx-auto flex h-full max-w-[386px] flex-col justify-between">
          <div className="gap-lg flex flex-col">
            <img src={logo} className="size-2xl mx-auto" />
            <div>
              <Typography step="3" as="h1" autoSpacing>
                {t("signUp:title")}
              </Typography>
              <Typography step="0" as="p" className="text-grayscale">
                {t("signUp:subtitle")}
              </Typography>
            </div>
            <div>
              <TextField
                id="signUpEmailField"
                label={t("signUp:email")}
                onChange={handleEmailChange}
                fullWidth
                type="email"
                name="email"
                error={hasEmailError ? t("signUp:emailError") : undefined}
                onFocus={() => setHasEmailError(false)}
              />
            </div>
            <div>
              <Button fullWidth className="mb-xs" onClick={handleContinue}>
                {t("continue")}
              </Button>
              <div className="gap-3xs flex items-center justify-center">
                <Typography step="-1" className="text-grayscale">
                  {t("signUp:alreadyHaveAnAccount")}
                </Typography>
                <Button shape="link" size="small" as="a" href="/users/sign_in">
                  {t("signUp:signIn")}
                </Button>
              </div>
            </div>
          </div>
          <Typography step="-1" as="p" className="text-grayscale text-center">
            <Trans
              t={t}
              i18nKey="signUp:CGU"
              components={{
                1: (
                  <Link
                    href="https://docs.google.com/document/d/1lNgCsCNnni156XmllauaSwL7KIsEOZT9"
                    size="medium"
                    className="text-sm"
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </Typography>
        </div>
        <Carousel />
        <img
          src={background}
          className="absolute top-0 left-0 z-[-1] h-full w-full scale-[1.02] object-cover blur-[5px]"
        />
      </div>
    );
  }

  return (
    <div className="gap-lg p-xl relative grid h-screen w-screen items-center justify-center overflow-hidden">
      <div className="animate-in slide-in-from-right-100 duration-1000">
        <Typography step="3" as="h1">
          La suite arrive bientôt !
        </Typography>
        <img src="https://www.animatedimages.org/data/media/1664/animated-work-in-progress-image-0023.gif" />
      </div>
      <img
        src={background}
        className="absolute top-0 left-0 z-[-1] h-full w-full scale-[1.02] object-cover blur-[5px]"
      />
    </div>
  );
};

export default SignUp;
